import { Box, Chip, Tooltip, Typography } from "@mui/material";
import RepeatIcon from "@mui/icons-material/Repeat";
import moment from "moment";
import {
  CALENDAR_DATE_FORMAT,
  JOB_STATUS_COLORS,
  JOB_STATUS_LABELS,
} from "../../../../constants";
import { JobRecurrence, JobStatus } from "../../../../enums";
import { JobModel } from "../../../../models";
import { Job } from "../../../../types";

const secondaryTextSx = {
  fontSize: 11,
  color: "var(--dashboard-text-secondary)",
};

type JobCardProps = {
  job: Job;
  hasConflict: boolean;
  isCompact: boolean;
  onClick: (job: Job) => void;
};

export const JobCard = ({
  job,
  hasConflict,
  isCompact,
  onClick,
}: JobCardProps) => {
  const crewNames = job.crew.map((member) => member.name).join(", ");
  const assignment = [job.truckCode, crewNames].filter(Boolean).join(" · ");
  const timeLabel = JobModel.getTimeRangeLabel(job);
  const isRecurring = job.recurrence !== JobRecurrence.None;

  return (
    <Tooltip
      title={
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {JobModel.getJobTitle(job)}
          </Typography>
          <Typography sx={{ fontSize: 11 }}>{timeLabel}</Typography>
          <Typography sx={{ fontSize: 11 }}>
            {JOB_STATUS_LABELS[job.status]}
          </Typography>
          {assignment && (
            <Typography sx={{ fontSize: 11 }}>{assignment}</Typography>
          )}
          {isRecurring && (
            <Typography sx={{ fontSize: 11 }}>
              {JobModel.getRecurrenceLabel(
                job.recurrence,
                moment(job.date, CALENDAR_DATE_FORMAT),
                job.recurrenceInterval,
              )}
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation();
          onClick(job);
        }}
        onKeyDown={(event) => event.key === "Enter" && onClick(job)}
        sx={{
          height: "100%",
          overflow: "hidden",
          px: 0.75,
          py: 0.5,
          cursor: "pointer",
          borderRadius: "6px",
          border: "1px solid var(--dashboard-border-tile)",
          borderLeft: `3px solid ${JOB_STATUS_COLORS[job.status]}`,
          backgroundColor: JobModel.isEvent(job)
            ? "var(--dashboard-track-segmented)"
            : "var(--dashboard-surface)",
          opacity: job.status === JobStatus.Canceled ? 0.6 : 1,
          "&:hover": { boxShadow: "var(--dashboard-shadow-tile-hover)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            noWrap
            sx={{
              flex: 1,
              fontSize: 12,
              fontWeight: 600,
              color: "var(--dashboard-text-primary)",
              textDecoration:
                job.status === JobStatus.Canceled ? "line-through" : "none",
            }}
          >
            {JobModel.getJobTitle(job)}
          </Typography>
          {isRecurring && (
            <RepeatIcon
              aria-label="Repeating job"
              sx={{ fontSize: 14, color: "var(--dashboard-text-secondary)" }}
            />
          )}
          {hasConflict && (
            <Chip
              label="Conflict"
              size="small"
              variant="outlined"
              color="error"
              sx={{ height: 18, fontSize: 10, borderRadius: "4px" }}
            />
          )}
        </Box>
        {!isCompact && (
          <>
            <Typography noWrap sx={secondaryTextSx}>
              {timeLabel}
            </Typography>
            {assignment && (
              <Typography noWrap sx={secondaryTextSx}>
                {assignment}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Tooltip>
  );
};
